/* Copyright (c) 2018-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, OnDestroy } from '@angular/core';
import { Observable, timer, Subscription, of, forkJoin, EMPTY, ReplaySubject, zip, Subject } from 'rxjs';
import { map, catchError, mergeMap, tap } from 'rxjs/operators';
import { isFunction, merge, isUndefined, extend, omit, isEqual, isNil, every, has } from 'lodash';
import { LoggingService, Logger } from 'crimson/lib/logging/logging.service';
import { DbCacheService } from 'crimson/lib/db/db-cache.service';
import { DbService } from 'crimson/lib/db/db.service';
import { NetworkStatusService } from 'crimson/lib/network-status-service/network-status.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import { JsEventsServerConfig } from '../config/js-events.config';
import {
    GdprConsentNone,
    JsEventsDefaultCommonData,
    EventsReturnCode,
    PlatformType
} from '../constants/js-events.constants';
import {
    JsEventsCommonData,
    DataCollectionConsent,
    UserConsentInfo,
    UnassignedStates,
    ConsentLevel,
    GdprLevel,
    EventMetadata,
    CallbackType,
    DataContext,
    CommonStoreData,
    RecordKey,
    EventsDbNames,
    FormattedEventDetail,
    SendMethodType,
    RecordKeyDbStates,
    CloudVariableStorageType,
    RequestParams,
    SdkMethodType,
    AllDbStates,
    Personalization
} from '../models/common-data.models';
import { EventsSdkService } from '../sdks/events-sdk.service';
import { TelemetryEvent } from '../telemetry/telemetry.models';
import { WlsHelperService } from './wls-helper-service';

@Injectable({
    providedIn: 'root'
})
export class JsEventsService implements OnDestroy {
    private logger: Logger;
    private commonData = JsEventsDefaultCommonData;
    private defaultConsent: DataCollectionConsent = GdprConsentNone;
    private deviceConsent: DataCollectionConsent = GdprConsentNone;
    private deviceConsentUpdated = false;
    private currentUserConsent: DataCollectionConsent = GdprConsentNone;
    private currentUserConsentUpdated = false;
    private currentUserId: string = UnassignedStates.Undefined;
    private syncedUserConsentInfo = false;
    private isOnline;
    private sendInterval$: Observable<number>;
    private sendEventSubscription: Subscription;
    private registeredErrorCallback: CallbackType = this.defaultErrorCallback;
    private cleanUpBacklogEvents = false;
    private sendBacklogEventsInitiated = false;
    private sendEventsInProgress = false;
    private eventsInBatch = false;
    private newEventInDb = false;
    private sendBatchEventsInProgress = {
        functional: false,
        technical: false,
        behavioral: false,
        required: false
    };
    private cleanBacklogEventsInProgress: Array<RecordKeyDbStates> = [];
    private cleanWlsBacklogEventsInProgress: Array<RecordKeyDbStates> = [];
    private customParams: RequestParams = { platform: false };
    private browserPlatform = false;
    private isTelemetryDisabled = false;
    private commonDataSubject = new ReplaySubject<JsEventsCommonData>(1);
    private serverLocale = 'unknown';
    private purgeInitiated = false;
    private purgeInProgress: AllDbStates = {
        commonDbIsClear: false,
        functionalDbIsClear: false,
        technicalDbIsClear: false,
        behavioralDbIsClear: false,
        requiredDbIsClear: false,
        wlsDbIsClear: false
    };

    getCommonData$(): Observable<JsEventsCommonData> {
        return this.commonDataSubject.asObservable();
    }

    get registeredErrorCallbackVal() {
        return this.registeredErrorCallback;
    }

    get defaultErrorCallbackVal() {
        return this.defaultErrorCallback;
    }

    get cleanUpBacklogEventsVal() {
        return this.cleanUpBacklogEvents;
    }

    get currentUserConsentUpdatedVal() {
        return this.currentUserConsentUpdated;
    }

    setDefaultConsentVal(val: DataCollectionConsent) {
        this.defaultConsent = val;
        this.deviceConsent = val;
    }

    setSendEventsInProgressVal(val: boolean) {
        this.sendEventsInProgress = val;
    }

    constructor(
        private config: JsEventsServerConfig,
        private eventSdkService: EventsSdkService,
        private logService: LoggingService,
        private dbCacheService: DbCacheService,
        private dbService: DbService,
        private networkStatusService: NetworkStatusService,
        private windowLocalStorage: WlsHelperService
    ) {
        this.logger = this.logService.getLogger('JsEventsService');
        this.commonData.clientId = this.config.clientId;
        this.commonData.eventSchemaVer = this.config.schemaVersion;
        this.sendInterval$ = timer(0, this.config.msBetweenSendRequest);
        this.logger.info('initialize', config);
        this.networkStatusService.onlineStatusChanged.subscribe(this.onOnlineStateChanged.bind(this));
        this.browserPlatform = PlatformDetectorService.isBrowserPlatform();
        this.serverLocale = this.eventSdkService.getServerLocale(null);
    }

    // error callbacks
    private defaultErrorCallback(param?: any): void {
        this.logger.error('message:', param && param.message ? param.message : 'Unknown error');
    }

    updateServer(serverUrl: string): void {
        const serverLocale: string = this.eventSdkService.setServer(serverUrl);
        this.onLocationStateChanged(serverLocale);
    }

    setErrorCallback = (errorCallback: CallbackType): void => {
        this.registeredErrorCallback = errorCallback;
    };

    // Handle sending events
    private sendToServerEndpoint(dataContext: DataContext, isImmediateRequest: boolean): Observable<object[]> {
        const eventsArray = dataContext.events.slice(0);
        dataContext.eventsToBeSent = dataContext.events.length;
        const sendToServerObservables: Observable<object>[] = [];
        while (dataContext.eventsToBeSent !== 0) {
            this.logger.event('eventsToBeSent', dataContext.eventsToBeSent.toString());
            let array: FormattedEventDetail[];
            if (dataContext.eventsToBeSent > this.config.maxEventsPerRequest) {
                array = eventsArray.slice(0, this.config.maxEventsPerRequest);
                eventsArray.splice(0, this.config.maxEventsPerRequest);
            } else {
                array = eventsArray;
            }
            dataContext.eventsToBeSent = dataContext.eventsToBeSent - array.length;
            const request = dataContext.commonData;
            request.events = array;
            request.sentTs = new Date().toISOString();
            const jsonString = JSON.stringify(request);
            this.logger.info('events request', jsonString);
            sendToServerObservables.push(
                this.eventSdkService.sendEvent(this.customParams, jsonString, isImmediateRequest)
            );
        }
        return forkJoin(sendToServerObservables);
    }

    //// backlog events
    private updateCleanupState(recordKey: string, dbGdprType: GdprLevel, state: boolean): void {
        let recordMatchFound = false;
        let indexId = 0;
        this.cleanBacklogEventsInProgress.forEach((recordItem: RecordKeyDbStates) => {
            if (recordItem.recordKey === recordKey) {
                recordMatchFound = true;
                recordItem.functionalDbIsClear =
                    dbGdprType === GdprLevel.Functional ? state : recordItem.functionalDbIsClear;
                recordItem.technicalDbIsClear =
                    dbGdprType === GdprLevel.Technical ? state : recordItem.technicalDbIsClear;
                recordItem.behavioralDbIsClear =
                    dbGdprType === GdprLevel.Behavioral ? state : recordItem.behavioralDbIsClear;
                recordItem.requiredDbIsClear = dbGdprType === GdprLevel.Required ? state : recordItem.requiredDbIsClear;
                if (
                    recordItem.functionalDbIsClear &&
                    recordItem.technicalDbIsClear &&
                    recordItem.behavioralDbIsClear &&
                    recordItem.requiredDbIsClear
                ) {
                    this.dbService.getGlobalStore(EventsDbNames.EventsCommonStore).removeItem(recordKey).subscribe();
                    this.cleanBacklogEventsInProgress.splice(indexId, 1);
                }
                indexId++;
            }
        });
        if (recordMatchFound === false) {
            const recordItem: RecordKeyDbStates = {
                recordKey,
                functionalDbIsClear: dbGdprType === GdprLevel.Functional ? state : false,
                technicalDbIsClear: dbGdprType === GdprLevel.Technical ? state : false,
                behavioralDbIsClear: dbGdprType === GdprLevel.Behavioral ? state : false,
                requiredDbIsClear: dbGdprType === GdprLevel.Required ? state : false
            };
            this.cleanBacklogEventsInProgress.push(recordItem);
        }
    }

    private getConsentedEvents(
        gdprLevel: GdprLevel,
        deviceConsent: DataCollectionConsent,
        userConsent: DataCollectionConsent,
        testDetailData: any
    ): FormattedEventDetail[] {
        const eventsArray: FormattedEventDetail[] = [];
        if (testDetailData && testDetailData.events && testDetailData.events.length > 0) {
            testDetailData.events.forEach(event => {
                const consentLevel: ConsentLevel = this.eventHasConsent(
                    event.personalization,
                    gdprLevel,
                    deviceConsent,
                    userConsent
                );
                if (consentLevel === ConsentLevel.Full || consentLevel === ConsentLevel.Temp) {
                    const eventDetail: FormattedEventDetail = extend({}, this.updatePersonalizationQualifiers(event));
                    eventsArray.push(eventDetail);
                } else {
                    this.logger.event(
                        'Event excluded from send due to no consent (to be discarded later): ',
                        event,
                        gdprLevel
                    );
                }
            });
        }
        return eventsArray;
    }

    private sendBacklogEventsDetailToServer(
        deviceConsent: DataCollectionConsent,
        userConsent: DataCollectionConsent,
        dbName: EventsDbNames,
        dbGdprType: GdprLevel,
        recordKey: string,
        testCommonData: CommonStoreData
    ): Observable<any> {
        this.logger.event(
            'attempting to send backlog events for dbName:',
            dbName,
            ' userconsent: ',
            userConsent,
            'key: ',
            recordKey
        );
        return this.dbService
            .getGlobalStore(dbName)
            .getItem(recordKey)
            .pipe(
                mergeMap(testDetailData => {
                    let dataContext: DataContext = {
                        commonData: {},
                        events: [],
                        eventsToBeSent: 0
                    };
                    const sendEventCallback: CallbackType = response => {
                        this.logger.event('jsEvents backlog response', response, dataContext.eventsToBeSent);
                        if (dataContext.eventsToBeSent === 0) {
                            this.dbService.getGlobalStore(dbName).removeItem(recordKey).subscribe();
                        }
                    };
                    const sendEventCatchCallback: CallbackType = err => {
                        this.logger.error('Failed to send backlog events', err.status);
                        this.logger.event(
                            'Failed to send backlog events for :',
                            recordKey,
                            dbName,
                            '.ErrorInfo: status',
                            err.status
                        );
                    };
                    dataContext = {
                        commonData: merge({}, testCommonData.common),
                        events: this.getConsentedEvents(dbGdprType, deviceConsent, userConsent, testDetailData),
                        eventsToBeSent: 0
                    };
                    if (dataContext.commonData && dataContext.events && dataContext.events.length > 0) {
                        return this.sendToServerEndpoint(dataContext, false).pipe(
                            map(responses => {
                                responses.forEach(response => {
                                    sendEventCallback(response);
                                });
                            }),
                            catchError(error => {
                                sendEventCatchCallback(error);
                                return EMPTY;
                            })
                        );
                    } else {
                        this.updateCleanupState(recordKey, dbGdprType, true);
                        this.dbService.getGlobalStore(dbName).removeItem(recordKey).subscribe();
                        return of(true);
                    }
                }),
                catchError(error => {
                    this.logger.event('detail data not available for backlog key', recordKey);
                    this.dbService.getGlobalStore(dbName).removeItem(recordKey).subscribe();
                    return of(true);
                })
            );
    }

    private updateWlsCleanupState(recordKey: string, dbGdprType: GdprLevel, state: boolean): void {
        let recordMatchFound = false;
        let indexId = 0;
        this.cleanWlsBacklogEventsInProgress.forEach((recordItem: RecordKeyDbStates) => {
            if (recordItem.recordKey === recordKey) {
                recordMatchFound = true;
                recordItem.functionalDbIsClear =
                    dbGdprType === GdprLevel.Functional ? state : recordItem.functionalDbIsClear;
                recordItem.technicalDbIsClear =
                    dbGdprType === GdprLevel.Technical ? state : recordItem.technicalDbIsClear;
                recordItem.behavioralDbIsClear =
                    dbGdprType === GdprLevel.Behavioral ? state : recordItem.behavioralDbIsClear;
                recordItem.requiredDbIsClear = dbGdprType === GdprLevel.Required ? state : recordItem.requiredDbIsClear;
                if (
                    recordItem.functionalDbIsClear &&
                    recordItem.technicalDbIsClear &&
                    recordItem.behavioralDbIsClear &&
                    recordItem.requiredDbIsClear
                ) {
                    this.windowLocalStorage.removeWlsItem(EventsDbNames.EventsCommonStore, recordKey);
                    this.cleanWlsBacklogEventsInProgress.splice(indexId, 1);
                }
                indexId++;
            }
        });
        if (recordMatchFound === false) {
            const recordItem: RecordKeyDbStates = {
                recordKey,
                functionalDbIsClear: dbGdprType === GdprLevel.Functional ? state : false,
                technicalDbIsClear: dbGdprType === GdprLevel.Technical ? state : false,
                behavioralDbIsClear: dbGdprType === GdprLevel.Behavioral ? state : false,
                requiredDbIsClear: dbGdprType === GdprLevel.Required ? state : false
            };
            this.cleanWlsBacklogEventsInProgress.push(recordItem);
        }
    }

    private sendWlsBacklogEventsDetailToServer(
        deviceConsent: DataCollectionConsent,
        userConsent: DataCollectionConsent,
        dbName: EventsDbNames,
        dbGdprType: GdprLevel,
        recordKey: string,
        testCommonData: CommonStoreData
    ): Observable<any> {
        this.logger.event(
            'attempting to send wls backlog events for dbName:',
            dbName,
            ' userconsent: ',
            userConsent,
            'key: ',
            recordKey
        );
        const testDetailData = this.windowLocalStorage.getWlsItem(dbName, recordKey);
        if (testDetailData) {
            let dataContext: DataContext = {
                commonData: {},
                events: [],
                eventsToBeSent: 0
            };
            const sendEventCallback: CallbackType = response => {
                this.logger.event('jsEvents wls backlog response', response, dataContext.eventsToBeSent);
                if (dataContext.eventsToBeSent === 0) {
                    return this.windowLocalStorage.removeWlsItem(dbName, recordKey);
                }
            };
            const sendEventCatchCallback: CallbackType = err => {
                this.logger.error('Failed to send wls backlog events :', err.status);
                this.logger.event(
                    'Failed to send wls backlog events for :',
                    recordKey,
                    dbName,
                    '.ErrorInfo: status',
                    err.status
                );
            };
            dataContext = {
                commonData: merge({}, testCommonData.common),
                events: this.getConsentedEvents(dbGdprType, deviceConsent, userConsent, testDetailData),
                eventsToBeSent: 0
            };
            if (dataContext.commonData && dataContext.events && dataContext.events.length > 0) {
                return this.sendToServerEndpoint(dataContext, false).pipe(
                    map(responses => {
                        responses.forEach(response => {
                            sendEventCallback(response);
                        });
                    }),
                    catchError(error => {
                        sendEventCatchCallback(error);
                        return EMPTY;
                    })
                );
            } else {
                this.updateWlsCleanupState(recordKey, dbGdprType, true);
                return of(this.windowLocalStorage.removeWlsItem(dbName, recordKey));
            }
        } else {
            this.updateWlsCleanupState(recordKey, dbGdprType, true);
            this.logger.event('detail data not available for wls backlog key', recordKey);
            return of(this.windowLocalStorage.removeWlsItem(dbName, recordKey));
        }
    }

    private attemptSendFromAllEventStores(
        sendMethod: SendMethodType,
        deviceConsent: DataCollectionConsent,
        userConsentFromCache: DataCollectionConsent,
        recordKey: string,
        testCommonData: CommonStoreData
    ): Observable<any> {
        return forkJoin([
            sendMethod.call(
                this,
                deviceConsent,
                userConsentFromCache,
                EventsDbNames.EventsDetailStore,
                GdprLevel.Functional,
                recordKey,
                testCommonData
            ),
            sendMethod.call(
                this,
                deviceConsent,
                userConsentFromCache,
                EventsDbNames.EventsDetailStoreTechnical,
                GdprLevel.Technical,
                recordKey,
                testCommonData
            ),
            sendMethod.call(
                this,
                deviceConsent,
                userConsentFromCache,
                EventsDbNames.EventsDetailStoreBehavioral,
                GdprLevel.Behavioral,
                recordKey,
                testCommonData
            ),
            sendMethod.call(
                this,
                deviceConsent,
                userConsentFromCache,
                EventsDbNames.EventsDetailStoreRequired,
                GdprLevel.Required,
                recordKey,
                testCommonData
            )
        ]);
    }

    private sendBacklogEvents(recordKey: string): void {
        try {
            this.dbService
                .getGlobalStore(EventsDbNames.EventsCommonStore)
                .getItem(recordKey)
                .pipe(
                    mergeMap((testCommonData: CommonStoreData) => {
                        this.logger.event('testCommonData', testCommonData);
                        if (testCommonData && testCommonData.common) {
                            const keyObj = JSON.parse(recordKey);
                            return this.getUserConsentFromCache(keyObj.userId || 'undefined', false).pipe(
                                mergeMap((userConsentFromCache: DataCollectionConsent) => {
                                    this.logger.event('userConsentFromCache', userConsentFromCache);
                                    // send time consent to be associated
                                    testCommonData.common.gdprFuncOptIn = this.getString(
                                        userConsentFromCache.functional
                                    );
                                    testCommonData.common.gdprTechOptIn = this.getString(
                                        userConsentFromCache.technical
                                    );
                                    testCommonData.common.gdprBehOptIn = this.getString(
                                        userConsentFromCache.behavioral
                                    );
                                    testCommonData.common.deviceGdprFuncOptIn = this.getString(
                                        this.deviceConsent.functional
                                    );
                                    testCommonData.common.deviceGdprTechOptIn = this.getString(
                                        this.deviceConsent.technical
                                    );
                                    testCommonData.common.deviceGdprBehOptIn = this.getString(
                                        this.deviceConsent.behavioral
                                    );
                                    return this.attemptSendFromAllEventStores(
                                        this.sendBacklogEventsDetailToServer,
                                        this.deviceConsent,
                                        userConsentFromCache,
                                        recordKey,
                                        testCommonData
                                    );
                                })
                            );
                        } else {
                            this.logger.event('common data not available for backlog key', recordKey);
                            this.dbService
                                .getGlobalStore(EventsDbNames.EventsCommonStore)
                                .removeItem(recordKey)
                                .subscribe();
                            return of(true);
                        }
                    }),
                    catchError(error => {
                        this.logger.event('common data not available for backlog key', recordKey);
                        this.dbService
                            .getGlobalStore(EventsDbNames.EventsCommonStore)
                            .removeItem(recordKey)
                            .subscribe();
                        return of(true);
                    })
                )
                .subscribe();
        } catch (error) {
            this.logger.error('catch backlog::', error);
        }
    }

    private sendWlsBacklogEvents(recordKey: string): void {
        try {
            const wlsCommonData = this.windowLocalStorage.getWlsItem(EventsDbNames.EventsCommonStore, recordKey);
            this.logger.event('wlsCommonData', wlsCommonData);
            if (wlsCommonData) {
                const keyObj = JSON.parse(recordKey);
                this.getUserConsentFromCache(keyObj.userId || 'undefined', false)
                    .pipe(
                        mergeMap((userConsentFromCache: DataCollectionConsent) => {
                            this.logger.event('userConsentFromCache', userConsentFromCache);
                            const testCommonData = {
                                common: wlsCommonData
                            };
                            testCommonData.common.gdprFuncOptIn = this.getString(userConsentFromCache.functional);
                            testCommonData.common.gdprTechOptIn = this.getString(userConsentFromCache.technical);
                            testCommonData.common.gdprBehOptIn = this.getString(userConsentFromCache.behavioral);
                            testCommonData.common.deviceGdprFuncOptIn = this.getString(this.deviceConsent.functional);
                            testCommonData.common.deviceGdprTechOptIn = this.getString(this.deviceConsent.technical);
                            testCommonData.common.deviceGdprBehOptIn = this.getString(this.deviceConsent.behavioral);
                            return this.attemptSendFromAllEventStores(
                                this.sendWlsBacklogEventsDetailToServer,
                                this.deviceConsent,
                                userConsentFromCache,
                                recordKey,
                                testCommonData
                            );
                        })
                    )
                    .subscribe();
            } else {
                this.logger.event('wls common data not available for backlog key', recordKey);
                this.windowLocalStorage.removeWlsItem(EventsDbNames.EventsCommonStore, recordKey);
            }
        } catch (error) {
            this.logger.error('catch wls backlog:', error);
        }
    }

    private setInProgressState(dbGdprType: GdprLevel, state: boolean): void {
        this.sendBatchEventsInProgress.functional =
            dbGdprType === GdprLevel.Functional ? state : this.sendBatchEventsInProgress.functional;
        this.sendBatchEventsInProgress.technical =
            dbGdprType === GdprLevel.Technical ? state : this.sendBatchEventsInProgress.technical;
        this.sendBatchEventsInProgress.behavioral =
            dbGdprType === GdprLevel.Behavioral ? state : this.sendBatchEventsInProgress.behavioral;
        this.sendBatchEventsInProgress.required =
            dbGdprType === GdprLevel.Required ? state : this.sendBatchEventsInProgress.required;
        // if there are none of the events being sent it means there are no more events in batch
        this.eventsInBatch = this.getInProgressState();
    }

    private getInProgressState(): boolean {
        return (
            this.sendBatchEventsInProgress.functional ||
            this.sendBatchEventsInProgress.technical ||
            this.sendBatchEventsInProgress.behavioral ||
            this.sendBatchEventsInProgress.required
        );
    }

    //// batched events (always currently logged in user requests reach here)
    private sendBatchedEventsDetailToServer(
        deviceConsent: DataCollectionConsent,
        userConsent: DataCollectionConsent,
        dbName: EventsDbNames,
        dbGdprType: GdprLevel,
        recordKey: string,
        testCommonData: CommonStoreData
    ): Observable<boolean> {
        this.logger.event(
            'attempting to send batched events for dbName:',
            dbName,
            ' userconsent: ',
            userConsent,
            'key: ',
            recordKey
        );
        const testDetailData: any = this.dbCacheService.getCachedGlobalItem(dbName, recordKey);
        let dataContext: DataContext = {
            commonData: {},
            events: [],
            eventsToBeSent: 0
        };
        const sendEventCallback: CallbackType = response => {
            this.logger.event('jsEvents batched response', response);
            const jsonObj: JsEventsCommonData = JSON.parse(response.config.data);
            this.cleanUpSentEvents(jsonObj.events, dbName, recordKey);
            this.setInProgressState(dbGdprType, false);
            this.sendEventsInProgress = false;
        };
        const sendEventCatchCallback = err => {
            this.setInProgressState(dbGdprType, false);
            this.sendEventsInProgress = false;
            this.logger.error('Failed to send events for :', recordKey, '.ErrorInfo: status', err.status);
        };
        dataContext = {
            commonData: merge({}, testCommonData.common),
            events: this.getConsentedEvents(dbGdprType, deviceConsent, userConsent, testDetailData),
            eventsToBeSent: 0
        };
        if (dataContext.commonData && dataContext.events && dataContext.events.length > 0) {
            this.setInProgressState(dbGdprType, true);
            this.sendEventsInProgress = true;
            this.sendToServerEndpoint(dataContext, false).subscribe(
                responses => {
                    responses.forEach(response => {
                        sendEventCallback(response);
                    });
                },
                error => {
                    sendEventCatchCallback(error);
                }
            );
        } else {
            if (testDetailData && testDetailData.events) {
                testDetailData.events.splice(0, testDetailData.events.length);
                testDetailData.persist_().subscribe();
            }
            this.setInProgressState(dbGdprType, false);
            this.sendEventsInProgress = false;
        }
        return EMPTY;
    }

    private sendEvents(): void {
        const recordKey = this.getRecordKey();
        // minimal logging as it will log every 5 seconds
        const cacheCommonData: any = this.dbCacheService.getCachedGlobalItem(
            EventsDbNames.EventsCommonStore,
            recordKey
        );
        if (cacheCommonData && cacheCommonData.common) {
            this.getUserConsentFromCache(this.currentUserId || UnassignedStates.Undefined, true)
                .pipe(
                    mergeMap((userConsentFromCache: DataCollectionConsent) => {
                        // send time consent to be associated
                        cacheCommonData.common.gdprFuncOptIn = this.getString(userConsentFromCache.functional);
                        cacheCommonData.common.gdprTechOptIn = this.getString(userConsentFromCache.technical);
                        cacheCommonData.common.gdprBehOptIn = this.getString(userConsentFromCache.behavioral);
                        cacheCommonData.common.deviceGdprFuncOptIn = this.getString(this.deviceConsent.functional);
                        cacheCommonData.common.deviceGdprTechOptIn = this.getString(this.deviceConsent.technical);
                        cacheCommonData.common.deviceGdprBehOptIn = this.getString(this.deviceConsent.behavioral);
                        return this.attemptSendFromAllEventStores(
                            this.sendBatchedEventsDetailToServer,
                            this.deviceConsent,
                            userConsentFromCache,
                            recordKey,
                            cacheCommonData
                        );
                    })
                )
                .subscribe();
        } else {
            this.logger.event('common data not available for key', recordKey);
            if (cacheCommonData && isUndefined(cacheCommonData.common)) {
                try {
                    this.registeredErrorCallback('common data not set before starting to send events');
                } catch (e) {
                    this.logger.error(e);
                    this.defaultErrorCallback(
                        'Invalid callback. Original Error: common data not set before starting to send events'
                    );
                }
            }
            this.sendEventsInProgress = false;
        }
    }

    // Handle timer
    private startTimeBasedSendToServer(): void {
        this.stopTimeBasedSendToServer();
        this.sendEventSubscription = this.sendInterval$.subscribe(() => {
            // WIP commented: the sendEventCallBack is not getting called in
            // sendBatchedEventsDetailToServer on a success. The same
            // events are getting sent every 5 seconds as a result.
            // i.e. cleanUpSentevents not getting called on success.
            // No repro of above scenario now, but keeping the comment for history
            // Dont attempt send events if there are no events to send.
            this.logger.event('events in batch:', this.eventsInBatch);
            this.logger.event('new event in db:', this.newEventInDb);
            if (
                (this.eventsInBatch === true || this.newEventInDb === true) &&
                this.sendEventsInProgress === false &&
                this.getInProgressState() === false &&
                this.getPurgeInProgressState() === false
            ) {
                this.newEventInDb = false;
                this.sendEventsInProgress = true;
                this.sendEvents();
            }
        });
        this.logger.event('send interval request made');
    }

    private stopTimeBasedSendToServer(): void {
        if (this.sendEventSubscription) {
            this.sendEventSubscription.unsubscribe();
            this.sendEventSubscription = null;
            this.logger.event('send interval stopped');
        }
    }

    // Handle Offline states
    private onOnlineStateChanged(isOnline: boolean) {
        this.isOnline = isOnline;
        if (!this.isOnline) {
            this.logger.info('jsEvents turns into offline');
            this.stopTimeBasedSendToServer();
        } else if (this.deviceConsent !== GdprConsentNone) {
            this.logger.info('jsEvents turns into online');
            this.startTimeBasedSendToServer();
        }
    }

    // Handle Locale/location states
    private onLocationStateChanged(serverLocale: string) {
        if (this.serverLocale !== 'unknown' && this.serverLocale !== serverLocale) {
            this.logger.info('purge stored events');
            this.purgeInitiated = true;
            this.purgeEventStoreRecords();
        }
        this.serverLocale = serverLocale;
    }

    private purgeEventStoreRecords() {
        this.purgeInProgress = {
            commonDbIsClear: false,
            functionalDbIsClear: false,
            technicalDbIsClear: false,
            behavioralDbIsClear: false,
            requiredDbIsClear: false,
            wlsDbIsClear: false
        };

        this.dbService
            .getGlobalStore(EventsDbNames.EventsDetailStore)
            .clear()
            .subscribe(() => {
                this.purgeInProgress.functionalDbIsClear = true;
            });
        this.dbService
            .getGlobalStore(EventsDbNames.EventsDetailStoreTechnical)
            .clear()
            .subscribe(() => {
                this.purgeInProgress.technicalDbIsClear = true;
            });
        this.dbService
            .getGlobalStore(EventsDbNames.EventsDetailStoreBehavioral)
            .clear()
            .subscribe(() => {
                this.purgeInProgress.behavioralDbIsClear = true;
            });
        this.dbService
            .getGlobalStore(EventsDbNames.EventsDetailStoreRequired)
            .clear()
            .subscribe(() => {
                this.purgeInProgress.requiredDbIsClear = true;
            });
        this.dbService
            .getGlobalStore(EventsDbNames.EventsCommonStore)
            .clear()
            .subscribe(() => {
                this.purgeInProgress.commonDbIsClear = true;
            });
        this.windowLocalStorage.clearall(EventsDbNames.EventsDetailStore);
        this.windowLocalStorage.clearall(EventsDbNames.EventsDetailStoreTechnical);
        this.windowLocalStorage.clearall(EventsDbNames.EventsDetailStoreBehavioral);
        this.windowLocalStorage.clearall(EventsDbNames.EventsDetailStoreRequired);
        this.windowLocalStorage.clearall(EventsDbNames.EventsCommonStore);
        this.purgeInProgress.wlsDbIsClear = true;
    }

    private getPurgeInProgressState(): boolean {
        let status = false;
        if (this.purgeInitiated) {
            status = !(
                this.purgeInProgress.wlsDbIsClear &&
                this.purgeInProgress.functionalDbIsClear &&
                this.purgeInProgress.technicalDbIsClear &&
                this.purgeInProgress.behavioralDbIsClear &&
                this.purgeInProgress.requiredDbIsClear &&
                this.purgeInProgress.commonDbIsClear
            );
            this.purgeInitiated = status;
        }
        return status;
    }

    // Handle user consent
    private getUserConsentFromCache(
        requestedUserId: string,
        isCurrentUser: boolean
    ): Observable<DataCollectionConsent> {
        const recordKey = requestedUserId || UnassignedStates.Undefined;
        const loggedOutUserConsent = {
            required: this.deviceConsent.required,
            functional: this.deviceConsent.functional,
            technical: ConsentLevel.None,
            behavioral: ConsentLevel.None
        };
        const userConsentData: any = this.dbCacheService.getOrCreateCachedGlobalItem(
            EventsDbNames.UserDataConsentStore,
            recordKey
        );
        if (userConsentData) {
            return userConsentData.sync_().pipe(
                map(() => {
                    const userConsent = userConsentData && userConsentData.userConsent;
                    if (isCurrentUser === true) {
                        if (!isNil(userConsent)) {
                            this.currentUserConsent = userConsent;
                            this.currentUserConsentUpdated = true;
                        } else if (this.currentUserId === UnassignedStates.Undefined) {
                            this.currentUserConsent = loggedOutUserConsent;
                            this.currentUserConsentUpdated = true;
                        }
                    }
                    return userConsent || loggedOutUserConsent;
                }),
                catchError(error => of(loggedOutUserConsent))
            );
        }
        return of(loggedOutUserConsent);
    }

    syncUserConsentInfo(userConsents: UserConsentInfo[]): void {
        const currentUserInfo = userConsents.find(consentItem => consentItem.userId === this.currentUserId);
        if (currentUserInfo) {
            this.currentUserConsent = currentUserInfo.userConsent;
            this.currentUserConsentUpdated = true;
        }

        userConsents.forEach((consentItem: UserConsentInfo) => {
            const cachedUserConsentData: any = this.dbCacheService.getOrCreateCachedGlobalItem(
                EventsDbNames.UserDataConsentStore,
                consentItem.userId
            );
            this.logger.event('previously cached consent', cachedUserConsentData.userConsent);
            cachedUserConsentData.userConsent = consentItem.userConsent;
            cachedUserConsentData.persist_().subscribe();
            this.logger.event('updated consent', consentItem.userConsent);
        });
        this.syncedUserConsentInfo = true;
        this.startSendingEvents();
    }

    setDefaultConsent(paramDefaultConsent: DataCollectionConsent): void {
        this.defaultConsent = paramDefaultConsent;
        this.deviceConsent = paramDefaultConsent;
        // device consent to be used in following cases
        if (this.currentUserId === UnassignedStates.Undefined || isUndefined(this.currentUserId)) {
            const loggedOutUserConsent = {
                required: this.deviceConsent.required,
                functional: this.deviceConsent.functional,
                technical: ConsentLevel.None,
                behavioral: ConsentLevel.None
            };
            this.currentUserConsent = loggedOutUserConsent;
            this.currentUserConsentUpdated = true;
        }
        this.startSendingEvents();
    }

    setDeviceConsent(deviceConsent: DataCollectionConsent): void {
        this.deviceConsent = deviceConsent;
        this.deviceConsentUpdated = true;
        this.logger.event('device consent', deviceConsent);
        this.startSendingEvents();
    }

    private eventHasConsent(
        eventPersonalization: Personalization,
        eventGdprLevel: GdprLevel,
        deviceConsent: DataCollectionConsent,
        userConsent: DataCollectionConsent
    ): ConsentLevel {
        let consentState: DataCollectionConsent;
        if (
            eventPersonalization === Personalization.Anonymous ||
            eventPersonalization === Personalization.DeviceOnly ||
            eventPersonalization === Personalization.DevicePreferred
        ) {
            //use device consent
            consentState = deviceConsent;
        } else if (eventPersonalization === Personalization.UserRequired) {
            //No consent if user not provided
            if (this.currentUserId === UnassignedStates.Undefined || isUndefined(this.currentUserId)) {
                consentState = GdprConsentNone;
            } else {
                consentState = userConsent;
            }
        } else {
            // this scenario maps to Personalization.UserPreferred or no personalization is set
            // use user consent if user not undefined else
            // use device consents valid for userPreferred case i.e. required and functional only
            if (this.currentUserId === UnassignedStates.Undefined || isUndefined(this.currentUserId)) {
                // changes for bug 2669055
                const loggedOutUserConsent = {
                    required: this.deviceConsent.required,
                    functional: this.deviceConsent.functional,
                    technical: ConsentLevel.None,
                    behavioral: ConsentLevel.None
                };
                consentState = loggedOutUserConsent;
            } else {
                // how to handle user logged in but consent not synced yet
                consentState = userConsent;
            }
        }
        if (eventGdprLevel === GdprLevel.Required) {
            return consentState.required;
        }
        if (eventGdprLevel === GdprLevel.Functional) {
            return consentState.functional;
        }
        if (eventGdprLevel === GdprLevel.Technical) {
            return consentState.technical;
        }
        if (eventGdprLevel === GdprLevel.Behavioral) {
            return consentState.behavioral;
        }
        return ConsentLevel.None;
    }

    // Handle cleanup store
    private cleanUpDbStore(): void {
        if (this.isTelemetryDisabled) {
            return;
        }

        const self = this;
        if (this.commonData.sessionId === UnassignedStates.Undefined) {
            self.logger.info('Cleanup cannot proceed, this.commonData.sessionId is undefined');
            return;
        }
        this.sendBacklogEventsInitiated = true;
        const currentKey: RecordKey = JSON.parse(this.getRecordKey());
        this.logger.info('cleanUpDbStore excluding session for', currentKey);
        try {
            self.dbService
                .getGlobalStore(EventsDbNames.EventsCommonStore)
                .keys()
                .pipe(
                    tap((keys: string[]) => {
                        keys.forEach((iterKey, iterationNumber) => {
                            try {
                                self.logger.event('Key#', iterationNumber, 'key ', iterKey);
                                if (iterKey === '_version') {
                                    self.logger.info('_version to be skipped', iterKey);
                                } else {
                                    const keyObj = JSON.parse(iterKey);
                                    if (keyObj.sessionId === '' || keyObj.sessionId === currentKey.sessionId) {
                                        self.logger.info('key not to be processed', keyObj);
                                    } else {
                                        self.sendBacklogEvents(iterKey);
                                    }
                                }
                            } catch (err) {
                                self.logger.error('Json parsing failed for key:', iterKey, 'error:', err?.message);
                            }
                        });
                    })
                )
                .subscribe();
        } catch (err) {
            self.logger.error('encountered failure, error:', err?.message);
        }
    }

    private cleanUpWlsStore() {
        this.sendBacklogEventsInitiated = true;
        const currentKey: RecordKey = JSON.parse(this.getRecordKey());
        this.logger.info('cleanUpWlsStore excluding session for', currentKey);

        for (let i = 0; i < this.windowLocalStorage.getLength(); ++i) {
            const key: string = this.windowLocalStorage.getKey(i);
            if (key.match('^' + EventsDbNames.EventsCommonStore)) {
                this.logger.event('Key#', i, 'key ', key);
                const keyObj = JSON.parse(key.replace(EventsDbNames.EventsCommonStore, ''));
                if (keyObj.sessionId === '' || keyObj.sessionId === currentKey.sessionId) {
                    this.logger.event('key not to be processed', keyObj);
                } else {
                    this.sendWlsBacklogEvents(JSON.stringify(keyObj));
                }
            }
        }
    }

    //This logic depends on http call returning the request event data in it's response.config.data
    private cleanUpSentEvents(events: FormattedEventDetail[], dbName: EventsDbNames, recordKey: string): void {
        this.logger.event('cleanUpSentEvents for', recordKey);

        const testDetailData: any = this.dbCacheService.getCachedGlobalItem(dbName, recordKey);

        events.forEach(event => {
            if (testDetailData && testDetailData.events.length > 0) {
                // find matching timestamp event in cache
                const match = testDetailData.events.find(eventItem => eventItem.ts === event.ts);
                // check if IDs match
                if (match && isEqual(match.parameters, event.parameters)) {
                    const index = testDetailData.events.indexOf(match);
                    this.logger.event('matching event found', event);
                    testDetailData.events.splice(index, 1);
                    testDetailData.persist_().subscribe(); // keep testing this
                } else {
                    this.logger.event('matching event not found');
                }
            } else {
                this.logger.event('events seem to be already cleared');
            }
        });
        testDetailData.persist_().subscribe();
    }

    // Handle all event data
    private startSendingEvents(): void {
        if (this.isOnline && this.deviceConsent !== GdprConsentNone && this.serverLocale !== 'unknown') {
            // If application launches in offline mode or no functional consent or no server url,
            // don't start to send any events. TELEMETRY-2026
            this.startTimeBasedSendToServer();
            if (this.sendBacklogEventsInitiated === false) {
                this.cleanUpDbStore();
                this.cleanUpBacklogEvents = false;
            }
        } else {
            this.logger.info('Device offline or No Functional consent. No clean job happened.', this.deviceConsent);
        }
    }

    private updateGdprQualifiers(data: TelemetryEvent<any>): FormattedEventDetail {
        const retData = omit(data, 'gdprLevel');
        // if consent is to be associated per event then add here.
        // For now it is decided to be added in common data
        return retData;
    }

    private updatePersonalizationQualifiers(data: FormattedEventDetail): FormattedEventDetail {
        const retData = omit(data, 'personalization');
        return retData;
    }

    private updateGdprAndPersonalizationQualifiers(data: TelemetryEvent<any>): FormattedEventDetail {
        const retData = omit(data, ['gdprLevel', 'personalization']);
        // if consent is to be associated per event then add here.
        // For now it is decided to be added in common data
        return retData;
    }

    private getString(value: any): string {
        if (value && typeof value !== 'string') {
            return JSON.stringify(value);
        } else {
            return value || '';
        }
    }

    private getRecordKey(): string {
        return JSON.stringify({
            sessionId: this.commonData.sessionId || UnassignedStates.Undefined,
            userId: this.currentUserId || UnassignedStates.Undefined
        });
    }

    private getFormattedInputData<T>(data: TelemetryEvent<T>, eventMetadata?: EventMetadata): string {
        const jsonObj = extend({}, this.commonData);
        if (eventMetadata.anonymize === true) {
            jsonObj.deviceId = 'undefined';
            jsonObj.userId = 'undefined';
            jsonObj.sessionId = 'undefined';
            jsonObj.externalUserId = 'undefined';
            jsonObj.idpId = 'undefined';
        }
        const retData: FormattedEventDetail = extend({}, this.updateGdprAndPersonalizationQualifiers(data));
        this.logger.event('eventDetail', retData);
        const arr = [];
        arr.push(retData);
        jsonObj.events = arr;
        jsonObj.sentTs = new Date().toISOString();
        // changes for bug 2578588
        jsonObj.gdprFuncOptIn = this.getString(this.currentUserConsent.functional);
        jsonObj.gdprTechOptIn = this.getString(this.currentUserConsent.technical);
        jsonObj.gdprBehOptIn = this.getString(this.currentUserConsent.behavioral);
        jsonObj.deviceGdprFuncOptIn = this.getString(this.deviceConsent.functional);
        jsonObj.deviceGdprTechOptIn = this.getString(this.deviceConsent.technical);
        jsonObj.deviceGdprBehOptIn = this.getString(this.deviceConsent.behavioral);
        return JSON.stringify(jsonObj);
    }

    private setFormattedEventsDetailData<T>(
        data: TelemetryEvent<T>,
        useLocalStorage: boolean,
        afterDone?: CallbackType
    ): void {
        const recordKey = this.getRecordKey();

        let dbName = EventsDbNames.EventsDetailStore;
        if (data.gdprLevel === GdprLevel.Technical) {
            dbName = EventsDbNames.EventsDetailStoreTechnical;
        }
        if (data.gdprLevel === GdprLevel.Behavioral) {
            dbName = EventsDbNames.EventsDetailStoreBehavioral;
        }
        if (data.gdprLevel === GdprLevel.Required) {
            dbName = EventsDbNames.EventsDetailStoreRequired;
        }

        let testDetailData: any;
        if (useLocalStorage) {
            // this can be true iff this.browserPlatform is true
            testDetailData = this.windowLocalStorage.getWlsItem(dbName, recordKey) || {};
            testDetailData.events = testDetailData.events || [];

            const eventDetail: FormattedEventDetail = extend({}, this.updateGdprQualifiers(data));
            this.logger.event('eventDetail wls', eventDetail);

            testDetailData.events.push(eventDetail);
            this.logger.event('details list wls', testDetailData.events);
            this.windowLocalStorage.setWlsItem(dbName, recordKey, testDetailData);
            if (isFunction(afterDone)) {
                afterDone();
            }
            this.logger.event('request to batch an exit time event');
            // this.newEventInDb = true; not needed since a send during exit may not pass.
        } else {
            testDetailData = this.dbCacheService.getOrCreateCachedGlobalItem(dbName, recordKey);
            let detailArray: FormattedEventDetail[] = testDetailData.events;

            const eventDetail: FormattedEventDetail = extend({}, this.updateGdprQualifiers(data));
            this.logger.event('eventDetail IDb', eventDetail);

            if (detailArray) {
                detailArray.push(eventDetail);
            } else {
                const arr1: FormattedEventDetail[] = [];
                arr1.push(eventDetail);
                detailArray = arr1;
            }
            this.logger.event('details list indexed db', detailArray);
            testDetailData.events = detailArray;
            testDetailData.persist_().subscribe(() => {
                if (isFunction(afterDone)) {
                    afterDone();
                }
                this.logger.event('request to batch an event');
                this.newEventInDb = true;
            });
        }
    }

    private storeEventDetail<T>(jsonObj: TelemetryEvent<T>, eventMetadata?: EventMetadata): void {
        this.setFormattedEventsDetailData(
            jsonObj,
            (this.browserPlatform && eventMetadata.appExit) || false,
            eventMetadata.afterDone || null
        );
    }

    private checkIfEventIsSendable<T>(jsonObj: TelemetryEvent<T>, eventMetadata?: EventMetadata): string {
        // block storing data as well in this case - No consent, no event gdprLevel.
        if (isEqual(this.deviceConsent, GdprConsentNone) || (jsonObj && isUndefined(jsonObj.gdprLevel))) {
            this.logger.info('No consent available, discarding event', jsonObj.name, jsonObj.gdprLevel);
            if (isFunction(eventMetadata.afterDone)) {
                eventMetadata.afterDone();
            }
            return EventsReturnCode.FunctionalConsentNotReceived;
        }
        if (this.getPurgeInProgressState()) {
            return EventsReturnCode.LocaleSwitchInProgress;
        }
        return EventsReturnCode.Ok;
    }

    /** This method receives client events for sending */
    sendEventDetail<T>(jsonObj: TelemetryEvent<T>, eventMetadata?: EventMetadata): string {
        if (jsonObj) {
            eventMetadata = merge({}, eventMetadata);
            if (!this.isValidPersonalization(jsonObj.personalization)) {
                jsonObj.personalization = Personalization.UserPreferred;
            }
            const returnVal = this.checkIfEventIsSendable(jsonObj, eventMetadata);
            if (returnVal !== EventsReturnCode.Ok) {
                return returnVal;
            }
            if (
                this.serverLocale !== 'unknown' &&
                (eventMetadata.immediateRequest === true || eventMetadata.useBeacon === true) &&
                this.isOnline === true
            ) {
                // event to be sent immediately
                const consentState = this.eventHasConsent(
                    jsonObj.personalization,
                    jsonObj.gdprLevel,
                    this.deviceConsent,
                    this.currentUserConsent
                );
                this.logger.event(
                    'attempting to send immediate event for userId:',
                    this.currentUserId,
                    ' userconsent: ',
                    this.currentUserConsent,
                    'deviceconsent: ',
                    this.deviceConsent
                );

                if (consentState === ConsentLevel.Full || consentState === ConsentLevel.Temp) {
                    const jsonString = this.getFormattedInputData(jsonObj, eventMetadata);
                    this.logger.info('jsonString request to send:', jsonString);
                    let sendToSdk: SdkMethodType = this.eventSdkService.sendEvent;
                    let isImmediateRequest = true;
                    if (eventMetadata.useBeacon) {
                        sendToSdk = this.eventSdkService.sendEventviaBeacon;
                        isImmediateRequest = false;
                    }
                    sendToSdk.call(this.eventSdkService, this.customParams, jsonString, isImmediateRequest).subscribe(
                        response => {
                            this.logger.event('jsevents response', response);
                            if (isFunction(eventMetadata.afterDone)) {
                                eventMetadata.afterDone();
                            }
                        },
                        error => {
                            this.logger.error(
                                'Failed to send eventdata for:',
                                jsonObj.name,
                                '.ErrorInfo: status',
                                error.status,
                                ', data',
                                error.config && error.config.data
                            );
                            this.storeEventDetail(jsonObj, eventMetadata);
                        }
                    );
                } else {
                    if (
                        this.currentUserId &&
                        this.currentUserId !== UnassignedStates.Undefined &&
                        this.currentUserConsentUpdated === false
                    ) {
                        this.storeEventDetail(jsonObj, eventMetadata);
                        this.logger.event(
                            'Event stored as userConsent not synced yet',
                            consentState,
                            this.currentUserConsentUpdated
                        );
                    } else {
                        if (isFunction(eventMetadata.afterDone)) {
                            eventMetadata.afterDone();
                        }
                        this.logger.error('Event cannot be sent due to consent level:', consentState);
                    }
                    return consentState;
                }
            } else {
                this.storeEventDetail(jsonObj, eventMetadata);
            }
            return EventsReturnCode.Ok;
        } else {
            this.logger.error('Empty request');
        }
        return EventsReturnCode.UnProcessed;
    }

    sendEventDetailWithPromise<T>(jsonObj: TelemetryEvent<T>, eventMetadata?: EventMetadata): Promise<string> {
        return new Promise((resolve, reject) => {
            if (jsonObj) {
                eventMetadata = merge({}, eventMetadata);
                // block storing data as well in this case - No Functional consent, no event gdprLevel.
                if (isEqual(this.deviceConsent, GdprConsentNone) || (jsonObj && isUndefined(jsonObj.gdprLevel))) {
                    this.logger.info(
                        'Functional consent not received, discarding event',
                        jsonObj.name,
                        jsonObj.gdprLevel
                    );
                    if (isFunction(eventMetadata.afterDone)) {
                        eventMetadata.afterDone();
                    }
                    reject(EventsReturnCode.FunctionalConsentNotReceived);
                    return;
                }
                if (this.getPurgeInProgressState()) {
                    reject(EventsReturnCode.LocaleSwitchInProgress);
                    return;
                }
                if (
                    this.serverLocale !== 'unknown' &&
                    (eventMetadata.immediateRequest === true || eventMetadata.useBeacon === true) &&
                    this.isOnline === true
                ) {
                    // event to be sent immediately
                    const consentState = this.eventHasConsent(
                        jsonObj.personalization,
                        jsonObj.gdprLevel,
                        this.deviceConsent,
                        this.currentUserConsent
                    );
                    this.logger.event(
                        'attempting to send immediate event for userId:',
                        this.currentUserId,
                        ' userconsent: ',
                        this.currentUserConsent,
                        'deviceconsent: ',
                        this.deviceConsent
                    );

                    if (consentState === ConsentLevel.Full || consentState === ConsentLevel.Temp) {
                        const jsonString = this.getFormattedInputData(jsonObj, eventMetadata);
                        let sendToSdk: SdkMethodType = this.eventSdkService.sendEvent;
                        let isImmediateRequest = true;
                        if (eventMetadata.useBeacon) {
                            sendToSdk = this.eventSdkService.sendEventviaBeacon;
                            isImmediateRequest = false;
                        }
                        sendToSdk
                            .call(this.eventSdkService, this.customParams, jsonString, isImmediateRequest)
                            .subscribe(
                                response => {
                                    this.logger.event('jsevents response', response);
                                    if (isFunction(eventMetadata.afterDone)) {
                                        eventMetadata.afterDone();
                                    }
                                    resolve(EventsReturnCode.Ok);
                                },
                                error => {
                                    this.logger.error(
                                        'Failed to send eventdata for:',
                                        jsonObj.name,
                                        '.ErrorInfo: status',
                                        error.status,
                                        ', data',
                                        error.config && error.config.data
                                    );
                                    this.storeEventDetail(jsonObj, eventMetadata);
                                    resolve(EventsReturnCode.UnProcessed);
                                }
                            );
                    } else {
                        if (
                            this.currentUserId &&
                            this.currentUserId !== UnassignedStates.Undefined &&
                            this.currentUserConsentUpdated === false
                        ) {
                            this.storeEventDetail(jsonObj, eventMetadata);
                            this.logger.event(
                                'Event stored as userConsent not synced yet',
                                consentState,
                                this.currentUserConsentUpdated
                            );
                        } else {
                            if (isFunction(eventMetadata.afterDone)) {
                                eventMetadata.afterDone();
                            }
                            this.logger.error('Event cannot be sent due to consent level:', consentState);
                        }
                        resolve(consentState);
                    }
                } else {
                    this.storeEventDetail(jsonObj, eventMetadata);
                    resolve(EventsReturnCode.Ok);
                }
            } else {
                this.logger.error('Empty request');
                resolve(EventsReturnCode.Ok);
            }
        });
    }

    private isValidPersonalization(personalization: string): boolean {
        if (
            personalization === Personalization.Anonymous ||
            personalization === Personalization.DeviceOnly ||
            personalization === Personalization.DevicePreferred ||
            personalization === Personalization.UserPreferred ||
            personalization === Personalization.UserRequired
        ) {
            return true;
        }
        return false;
    }

    setEventsCommonData(eventsCommonData: JsEventsCommonData): Observable<DataCollectionConsent> {
        if (!eventsCommonData) {
            this.logger.error('Undefined common data sent by client');
            return of(undefined);
        }

        this.cleanUpBacklogEvents = false;

        if (this.commonData.sessionId === UnassignedStates.Undefined && eventsCommonData.sessionId) {
            this.cleanUpBacklogEvents = true;
        }

        this.commonData = merge({}, this.commonData, eventsCommonData);
        this.logger.info(
            'client user data',
            eventsCommonData.userId,
            eventsCommonData.externalUserId,
            eventsCommonData.idpId
        );
        this.logger.info(
            'this.commonData user data',
            this.commonData.userId,
            this.commonData.externalUserId,
            this.commonData.idpId
        );
        this.commonData.eventProtocol = '1.6';
        if (this.commonData.idpId !== UnassignedStates.Undefined) {
            this.currentUserId = this.commonData.userId;
        } else {
            this.currentUserId = this.commonData.externalUserId;
        }
        this.currentUserConsentUpdated = false;
        this.logger.info('Device Id:', this.commonData.deviceId, 'set for client:', this.commonData.clientId);

        const consent$ = this.getUserConsentFromCache(this.currentUserId, true);
        this.logger.event(this.commonData);

        const recordKey = this.getRecordKey();
        const testCommonData: any = this.dbCacheService.getOrCreateCachedGlobalItem(
            EventsDbNames.EventsCommonStore,
            recordKey
        );
        testCommonData.common = this.commonData;
        testCommonData.persist_().subscribe();

        if (this.browserPlatform) {
            let wlsCommonData = this.windowLocalStorage.getWlsItem(EventsDbNames.EventsCommonStore, recordKey) || {};
            wlsCommonData = this.commonData;
            this.windowLocalStorage.setWlsItem(EventsDbNames.EventsCommonStore, recordKey, wlsCommonData);
        }

        if (this.isOnline && this.deviceConsent !== GdprConsentNone && this.serverLocale !== 'unknown') {
            // If application launches in offline mode or no functional consent or no server url,
            // don't start to send any events. TELEMETRY-2026
            this.startTimeBasedSendToServer();
            // clean up backlog events when sessionId is set for first time
            if (this.cleanUpBacklogEvents && this.sendBacklogEventsInitiated === false) {
                this.cleanUpDbStore();
                this.cleanUpBacklogEvents = false;
            }
            if (this.browserPlatform) {
                this.cleanUpWlsStore();
            }
        } else {
            this.logger.info('Device offline or No Functional consent. No clean job happened.', this.deviceConsent);
        }

        this.commonDataSubject.next(this.commonData);
        return consent$;
    }

    setOverrideToolTelemetryState(isDisabled: boolean) {
        this.isTelemetryDisabled = isDisabled;
    }

    ngOnDestroy() {
        if (this.sendEventSubscription) {
            this.sendEventSubscription.unsubscribe();
            this.sendEventSubscription = undefined;
        }
    }
}
